import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '@constants'
import publicApiClient from '@services/api/publicClient'
import ScrollToTopButton from '@components/common/ScrollToTopButton'
import { CtaBand, GOLD_ICON } from '@components/marketing'
import waveLogo from '@assets/icons/wave.png'
import orangeLogo from '@assets/icons/Orange.png'
import mtnLogo from '@assets/icons/mtn.png'
import moovLogo from '@assets/icons/moov-money.png'

const NAVY = '#002A4E'
const GOLD = '#FFD700'
const GOLD_TEXT = '#B8860B'

// Grille par défaut (identique au seeder) si l'API est injoignable
const DEFAULT_TARIFS = {
  I3: { 1: 100, 3: 300, 7: 500, 30: 2500 },
  I6: { 1: 200, 3: 500, 7: 1000, 30: 5000 },
  I9: { 1: 300, 3: 700, 7: 2000, 30: 7000 },
  V:  { 1: 1500, 3: 4000, 7: 8000, 30: 25000 },
  B:  { 1: 1000, 3: 3000, 7: 6000, 30: 20000 },
}

const DURATIONS = [
  { days: 1,  label: '1 jour' },
  { days: 3,  label: '3 jours' },
  { days: 7,  label: '1 semaine', popular: true },
  { days: 30, label: '1 mois' },
]

const ICONS = {
  image:  'M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A1.5 1.5 0 0021.75 19.5V4.5A1.5 1.5 0 0020.25 3H3.75A1.5 1.5 0 002.25 4.5v15A1.5 1.5 0 003.75 21zM15 8.25h.008v.008H15V8.25z',
  video:  'M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9A2.25 2.25 0 0013.5 5.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z',
  banner: 'M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38a1.125 1.125 0 01-1.536-.48 20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46',
  check:  'M4.5 12.75l6 6 9-13.5',
  arrow:  'M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3',
  clock:  'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z',
  gift:   'M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z',
  card:   'M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z',
  bolt:   'M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z',
  chevron:'M19.5 8.25l-7.5 7.5-7.5-7.5',
}

const Icon = ({ d, className = 'w-6 h-6', strokeWidth = 1.8 }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={strokeWidth} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
)

const FORMATS = [
  {
    key: 'images', label: 'Images', icon: 'image', route: ROUTES.CREATE_POST,
    desc: 'Présentez vos produits en photos, de 3 à 9 images par annonce.',
    features: (n) => [`${n} photos par annonce`, 'Titre, description et prix', 'Contact direct par messagerie', 'Vues, likes et commentaires'],
  },
  {
    key: 'videos', label: 'Vidéos', icon: 'video', route: ROUTES.CREATE_VIDEO, pkg: 'V',
    desc: 'Montrez vos produits en action avec une annonce vidéo.',
    features: () => ['1 vidéo par annonce', 'Miniature affichée dans le fil', 'Contact direct par messagerie', 'Vues, likes et commentaires'],
  },
  {
    key: 'banners', label: 'Bannières', icon: 'banner', route: ROUTES.CREATE_BANNER, pkg: 'B',
    desc: 'Mettez vos promotions et événements en avant sur la plateforme.',
    features: () => ['1 visuel publicitaire', 'Affiché dans les annonces sponsorisées', 'Lien vers votre offre', 'Suivi des vues'],
  },
]

const IMAGE_PACKS = [
  { n: 3, pkg: 'I3' },
  { n: 6, pkg: 'I6' },
  { n: 9, pkg: 'I9' },
]

const PAYMENTS = [
  { name: 'Wave', logo: waveLogo, available: true },
  { name: 'Orange Money', logo: orangeLogo },
  { name: 'MTN MoMo', logo: mtnLogo },
  { name: 'Moov Money', logo: moovLogo },
]

const FAQ = [
  { q: 'Faut-il un abonnement ?', a: 'Non. Vous payez uniquement les annonces que vous publiez, pour la durée de votre choix. Aucun engagement, aucun prélèvement automatique.' },
  { q: 'Puis-je publier gratuitement ?', a: 'Oui. À l\'inscription, vous bénéficiez d\'une période d\'essai pendant laquelle vous pouvez publier sans payer.' },
  { q: 'Comment payer ?', a: 'Le paiement se fait par Wave, directement depuis votre téléphone. Orange Money et MTN Mobile Money arrivent bientôt.' },
  { q: 'Quand mon annonce est-elle visible ?', a: 'Dès que le paiement est confirmé, votre annonce est en ligne pour toute la durée choisie.' },
  { q: 'Que se passe-t-il à la fin de la durée ?', a: 'L\'annonce n\'est plus diffusée. Vous pouvez la republier à tout moment en choisissant une nouvelle durée.' },
]

const fcfa = (n) => `${Number(n).toLocaleString('fr-FR')}`

function useInView(threshold = 0.12) {
  const ref = useRef(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); observer.disconnect() } },
      { threshold }
    )
    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [threshold])
  return [ref, inView]
}

const Reveal = ({ delay = 0, className = '', children, style, ...rest }) => {
  const [ref, inView] = useInView()
  return (
    <div
      ref={ref}
      className={`anim-hidden ${inView ? 'animate-fade-in-up' : ''} ${className}`}
      style={{ animationDelay: `${delay}ms`, ...style }}
      {...rest}
    >
      {children}
    </div>
  )
}

const PricingPage = () => {
  const navigate = useNavigate()
  const [tarifs, setTarifs] = useState(DEFAULT_TARIFS)
  const [activeFormat, setActiveFormat] = useState('images')
  const [imageCount, setImageCount] = useState(3)
  const [openFaq, setOpenFaq] = useState(0)

  useEffect(() => {
    publicApiClient.get('/tarifs')
      .then((res) => {
        if (!res?.success || !res.data) return
        const grid = {}
        Object.entries(res.data).forEach(([pkg, rows]) => {
          grid[pkg] = {}
          rows.forEach((t) => { grid[pkg][t.nb_jours] = Number(t.montant_Tarifs) })
        })
        setTarifs((prev) => ({ ...prev, ...grid }))
      })
      .catch(() => {})
  }, [])

  const format = FORMATS.find((f) => f.key === activeFormat)
  const pkg = activeFormat === 'images' ? IMAGE_PACKS.find((p) => p.n === imageCount).pkg : format.pkg
  const prices = tarifs[pkg] || DEFAULT_TARIFS[pkg]
  const features = format.features(imageCount)
  const dailyBase = prices[1]

  return (
    <div className="home-font bg-white text-slate-900 overflow-x-hidden">

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #FFFCF3 0%, #FFF9E6 35%, #FFFFFF 100%)' }}>
        <div className="absolute inset-0 home-grid-bg-light pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 45% 55% at 50% 45%, rgba(255,215,0,0.45) 0%, rgba(255,215,0,0.12) 55%, transparent 100%)' }} />

        <div className="relative w-[90%] md:w-[80%] mx-auto pt-16 pb-14 lg:pt-24 text-center">
          <div className="hero-badge-in inline-flex items-center gap-2 rounded-full border border-amber-200 bg-white/80 backdrop-blur px-4 py-1.5 text-sm font-medium text-slate-700 shadow-sm" style={{ opacity: 0 }}>
            <Icon d={ICONS.gift} className="w-4 h-4" />
            Période d&apos;essai gratuite à l&apos;inscription
          </div>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08]" style={{ color: NAVY }}>
            Des tarifs simples.
            <br />
            <span className="home-highlight px-1" style={{ color: '#C98F00' }}>Sans abonnement.</span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Payez uniquement l&apos;annonce que vous publiez, pour la durée de votre choix.
            À partir de <strong style={{ color: NAVY }}>{fcfa(tarifs.I3?.[1] ?? 100)} FCFA</strong>.
          </p>
        </div>
      </section>

      {/* ============ SÉLECTEUR + GRILLE ============ */}
      <section className="pb-24">
        <div className="w-[90%] md:w-[80%] mx-auto">

          {/* Onglets format */}
          <div className="flex justify-center">
            <div className="inline-flex w-full sm:w-auto rounded-md border border-slate-200 bg-white p-1.5 shadow-sm">
              {FORMATS.map((f) => {
                const active = f.key === activeFormat
                return (
                  <button
                    key={f.key}
                    onClick={() => setActiveFormat(f.key)}
                    className={`flex flex-1 sm:flex-none items-center justify-center gap-2 rounded-md px-4 sm:px-7 py-3 text-sm font-semibold transition-all ${
                      active ? 'shadow-md' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                    }`}
                    style={active ? { background: NAVY, color: '#fff' } : {}}
                  >
                    <Icon d={ICONS[f.icon]} className="w-5 h-5" />
                    {f.label}
                  </button>
                )
              })}
            </div>
          </div>

          <p className="mt-5 text-center text-slate-600">{format.desc}</p>

          {/* Nombre d'images */}
          {activeFormat === 'images' && (
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <span className="text-sm font-medium text-slate-500">Nombre de photos :</span>
              <div className="inline-flex rounded-md bg-slate-100 p-1">
                {IMAGE_PACKS.map((p) => (
                  <button
                    key={p.n}
                    onClick={() => setImageCount(p.n)}
                    className={`rounded-md px-5 py-2 text-sm font-bold transition-all ${imageCount === p.n ? 'bg-white shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                    style={imageCount === p.n ? { color: NAVY } : {}}
                  >
                    {p.n} photos
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Cartes durée */}
          <div key={`${activeFormat}-${imageCount}`} className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
            {DURATIONS.map((d, i) => {
              const price = prices[d.days]
              const perDay = price / d.days
              const saving = dailyBase ? Math.round((1 - perDay / dailyBase) * 100) : 0
              const popular = d.popular

              return (
                <div
                  key={d.days}
                  className={`animate-fade-in-up anim-hidden relative flex flex-col rounded-md p-7 transition-all duration-300 hover:-translate-y-1 ${
                    popular ? 'shadow-[0_25px_60px_-20px_rgba(245,183,0,0.7)] lg:-my-3 lg:py-10' : 'bg-white border border-slate-200 hover:shadow-xl hover:border-transparent'
                  }`}
                  style={{ animationDelay: `${i * 70}ms`, ...(popular ? { background: 'linear-gradient(135deg, #FFE45C 0%, #FFD700 45%, #F5B700 100%)' } : {}) }}
                >
                  {popular && (
                    <>
                      <div className="absolute inset-0 rounded-md home-grid-bg-light pointer-events-none" />
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-4 py-1 text-xs font-bold shadow-md" style={{ background: NAVY, color: GOLD }}>
                        Le plus choisi
                      </span>
                    </>
                  )}

                  <div className="relative flex items-center justify-between">
                    <span className={`inline-flex items-center gap-1.5 text-sm font-semibold ${popular ? 'text-[#002A4E]/70' : 'text-slate-500'}`}>
                      <Icon d={ICONS.clock} className="w-4 h-4" />
                      {d.label}
                    </span>
                    {saving > 0 && (
                      <span
                        className="rounded-full px-2.5 py-0.5 text-xs font-bold"
                        style={popular ? { background: NAVY, color: GOLD } : { background: '#ECFDF5', color: '#047857' }}
                      >
                        -{saving}%
                      </span>
                    )}
                  </div>

                  <div className="relative mt-6 flex items-baseline gap-1.5">
                    <span className="text-4xl lg:text-5xl font-extrabold tracking-tight" style={{ color: NAVY }}>
                      {fcfa(price)}
                    </span>
                    <span className={`text-sm font-semibold ${popular ? 'text-[#002A4E]/60' : 'text-slate-500'}`}>FCFA</span>
                  </div>
                  <p className={`relative mt-1 text-sm ${popular ? 'text-[#002A4E]/60' : 'text-slate-400'}`}>
                    {d.days > 1 ? `soit ${fcfa(Math.round(perDay))} FCFA / jour` : 'idéal pour tester'}
                  </p>

                  <div className={`relative my-6 h-px ${popular ? 'bg-[#002A4E]/10' : 'bg-slate-100'}`} />

                  <ul className="relative space-y-3 flex-1">
                    {features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm">
                        <span
                          className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full"
                          style={popular ? { background: '#FFFFFF', color: NAVY } : GOLD_ICON}
                        >
                          <Icon d={ICONS.check} className="w-3 h-3" strokeWidth={3} />
                        </span>
                        <span className={popular ? 'text-[#002A4E]/85' : 'text-slate-600'}>{f}</span>
                      </li>
                    ))}
                    <li className="flex items-start gap-2.5 text-sm">
                      <span
                        className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full"
                        style={popular ? { background: '#FFFFFF', color: NAVY } : GOLD_ICON}
                      >
                        <Icon d={ICONS.check} className="w-3 h-3" strokeWidth={3} />
                      </span>
                      <span className={popular ? 'text-[#002A4E]/85' : 'text-slate-600'}>Visible {d.label}</span>
                    </li>
                  </ul>

                  <button
                    onClick={() => navigate(format.route)}
                    className={`relative mt-8 inline-flex w-full items-center justify-center gap-2 rounded-md py-3.5 text-sm font-bold transition-all hover:-translate-y-0.5 ${
                      popular ? 'shadow-lg shadow-[#002A4E]/25' : 'border border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                    style={popular ? { background: NAVY, color: '#FFFFFF' } : { color: NAVY }}
                  >
                    Publier une annonce
                    <Icon d={ICONS.arrow} className="w-4 h-4" strokeWidth={2.2} />
                  </button>
                </div>
              )
            })}
          </div>

          <p className="mt-8 text-center text-sm text-slate-500">
            Prix en FCFA, payés une seule fois par annonce. Aucun frais caché.
          </p>
        </div>
      </section>

      {/* ============ COMMENT ÇA MARCHE ============ */}
      <section className="py-24 bg-slate-50">
        <div className="w-[90%] md:w-[80%] mx-auto">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="text-sm font-bold uppercase tracking-widest" style={{ color: GOLD_TEXT }}>Paiement</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: NAVY }}>
              Publiez et payez en 2 minutes.
            </h2>
          </Reveal>

          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {[
              { icon: 'image', title: 'Créez votre annonce', text: 'Ajoutez photos, vidéo ou bannière, puis décrivez votre offre.' },
              { icon: 'card', title: 'Choisissez la durée', text: 'De 1 jour à 1 mois. Le prix s\'affiche avant de payer.' },
              { icon: 'bolt', title: 'Payez et c\'est en ligne', text: 'Réglez avec Wave depuis votre téléphone : votre annonce est publiée.' },
            ].map((s, i) => (
              <Reveal key={s.title} delay={i * 100} className="rounded-md bg-white border border-slate-200/70 p-8">
                <div className="flex items-center gap-4">
                  <span className="flex h-12 w-12 items-center justify-center rounded-md" style={GOLD_ICON}>
                    <Icon d={ICONS[s.icon]} />
                  </span>
                  <span className="text-sm font-bold text-slate-400">Étape {i + 1}</span>
                </div>
                <h3 className="mt-6 text-lg font-bold" style={{ color: NAVY }}>{s.title}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{s.text}</p>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-10 flex flex-col md:flex-row items-center justify-between gap-6 rounded-md bg-white border border-slate-200/70 px-8 py-6">
            <p className="text-sm font-semibold" style={{ color: NAVY }}>Moyens de paiement</p>
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
              {PAYMENTS.map((p) => (
                <div key={p.name} className={`flex items-center gap-2.5 ${p.available ? '' : 'opacity-50 grayscale'}`}>
                  <img src={p.logo} alt={p.name} className="h-9 w-9 rounded-md object-contain" />
                  <div className="leading-tight">
                    <p className="text-sm font-semibold text-slate-700">{p.name}</p>
                    <p className={`text-xs ${p.available ? 'text-emerald-600 font-semibold' : 'text-slate-400'}`}>
                      {p.available ? 'Disponible' : 'Bientôt'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="py-24 bg-white">
        <div className="w-[90%] md:w-[80%] mx-auto">
          <Reveal className="text-center">
            <p className="text-sm font-bold uppercase tracking-widest" style={{ color: GOLD_TEXT }}>Questions fréquentes</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: NAVY }}>
              Tout ce qu&apos;il faut savoir.
            </h2>
          </Reveal>

          <div className="mt-12 divide-y divide-slate-200 border-y border-slate-200">
            {FAQ.map((item, i) => {
              const open = openFaq === i
              return (
                <div key={item.q}>
                  <button
                    onClick={() => setOpenFaq(open ? -1 : i)}
                    className="flex w-full items-center justify-between gap-6 py-5 text-left"
                    aria-expanded={open}
                  >
                    <span className="text-base sm:text-lg font-semibold" style={{ color: NAVY }}>{item.q}</span>
                    <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-all ${open ? 'rotate-180' : ''}`} style={{ background: open ? GOLD : '#F1F5F9', color: NAVY }}>
                      <Icon d={ICONS.chevron} className="w-4 h-4" strokeWidth={2.5} />
                    </span>
                  </button>
                  <div className={`grid transition-all duration-300 ${open ? 'grid-rows-[1fr] opacity-100 pb-5' : 'grid-rows-[0fr] opacity-0'}`}>
                    <p className="overflow-hidden text-slate-600 leading-relaxed pr-12">{item.a}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      <CtaBand
        title="Commencez gratuitement dès aujourd'hui."
        text="Créez votre compte et publiez vos premières annonces pendant la période d'essai, sans engagement."
        primary={{ to: ROUTES.REGISTER, label: 'Créer mon compte' }}
        secondary={{ to: '/contact', label: 'Une question ? Contactez-nous' }}
      />

      <ScrollToTopButton />
    </div>
  )
}

export default PricingPage
